// ===== Online telecronaca — faithful port of the CPU narration =====
// This module reproduces, VERBATIM, the announcement texts, order and TTS/audio
// behaviour of the offline CPU telecronaca (see useScopaGame.js processEvents +
// the post-CPU-turn cues). It does NOT reinterpret or simplify anything: it only
// changes the EVENT SOURCE. The offline game gets events synchronously from
// engine.applyMove(); online gets the SAME events array, forwarded by the
// authoritative server after each move. The engine and the CPU mode are untouched.
//
// Actor mapping: the engine uses generic actors 'player'/'cpu'. Offline, the human
// is always 'player'. Online, the local human is whichever seat they hold
// (A -> 'player', B -> 'cpu'); the opponent is the other actor. `myKey`/`oppKey`
// carry this mapping so the exact same strings are produced from each seat's POV.

import { cardLabel, cardsLabel } from '../game/cards';
import { playerName, isPartner, scoreSummary, teamNames, orderedSides, toName } from './multiLabels';

// Build the ordered list of narration items produced by a single applied move.
// Each item may carry: { sound, text+channel(+key), toast }. The hook consumes
// them in order (play sound, push to the aria-live queue, show sonner toast),
// exactly mirroring the CPU processEvents side effects.
export function buildMoveAnnouncements({ events, myKey, view }) {
  const items = [];
  for (const ev of events) {
    const isOpponent = ev.actor !== undefined && ev.actor !== myKey;
    if (ev.type === 'play') {
      items.push({ sound: 'playCard' });
      if (isOpponent) {
        items.push({ channel: 'telecronaca', text: `L'avversario ha giocato ${cardLabel(ev.played)}.` });
      }
    } else if (ev.type === 'capture') {
      items.push({ sound: ev.scopa ? 'scopa' : 'capture' });
      if (isOpponent) {
        if (ev.scopa) {
          items.push({
            channel: 'telecronaca',
            text: `L'avversario ha giocato ${cardLabel(ev.played)} e ha preso tutte le carte sul tavolo. Scopa!`,
          });
        } else {
          items.push({
            channel: 'telecronaca',
            text: `L'avversario ha giocato ${cardLabel(ev.played)} e ha preso ${cardsLabel(ev.captured)} con ${cardLabel(ev.played)}.`,
          });
        }
      } else if (ev.scopa) {
        items.push({
          channel: 'telecronaca',
          text: 'Scopa!',
          toast: { kind: 'success', title: 'Scopa!', description: 'Hai spazzato il tavolo!' },
        });
      }
      if (ev.scopa && isOpponent) {
        items.push({
          toast: { kind: 'default', title: 'Scopa dell\u2019avversario', description: 'L\u2019avversario ha spazzato il tavolo.' },
        });
      }
    } else if (ev.type === 'refill') {
      items.push({ channel: 'telecronaca', text: 'Nuove carte distribuite.' });
    } else if (ev.type === 'handOver') {
      const oppKey = myKey === 'player' ? 'cpu' : 'player';
      const p = ev.matchScores[myKey];
      const c = ev.matchScores[oppKey];
      items.push({ toast: { kind: 'default', title: 'Fine della mano', description: `Punteggio partita — Tu ${p}, Avversario ${c}.` } });
      items.push({ channel: 'telecronaca', text: `Fine della mano. Punteggio della partita: tu ${p}, avversario ${c}.` });
    } else if (ev.type === 'gameOver') {
      if (ev.winner === myKey) {
        items.push({ sound: 'win' });
        items.push({ channel: 'telecronaca', text: 'Hai vinto la partita!' });
      } else {
        items.push({ channel: 'telecronaca', text: 'L\u2019avversario ha vinto la partita.' });
      }
    }
  }

  // Post-opponent-turn cues — mirror of the CPU-driving effect in useScopaGame:
  // after the OPPONENT's move (only), report the remaining table and, if it is
  // now the local player's turn, "È il tuo turno." Player's own move never
  // triggers these (offline parity).
  const moveEv = events.find((e) => e.type === 'play' || e.type === 'capture');
  const isOpponentMove = moveEv && moveEv.actor !== myKey;
  if (isOpponentMove && view.phase === 'playing') {
    const t = view.table;
    const tableText = t.length === 0 ? 'Il tavolo è vuoto.' : `Sul tavolo rimangono ${cardsLabel(t)}.`;
    items.push({ channel: 'telecronaca', text: tableText, key: 'table' });
    if (view.turn === view.you) {
      items.push({ channel: 'status', text: 'È il tuo turno.', key: 'turn' });
    }
  }

  return items;
}

// Opening announcement — verbatim port of emitStartAnnouncement (offline).
// Returns the string to speak once at the very start of a match, or null.
export function startAnnouncement(view) {
  const freshStart =
    view.phase === 'playing' &&
    view.handNumber === 1 &&
    view.table.length === 4 &&
    view.yourHand.length === 3 &&
    view.opponentCardCount === 3;
  if (!freshStart) return null;
  const tableText =
    view.table.length === 0 ? 'Il tavolo è vuoto' : `Sul tavolo ci sono ${cardsLabel(view.table)}`;
  const turnText = view.turn === view.you ? 'È il tuo turno.' : "È il turno dell'avversario.";
  return `Inizia la partita. ${tableText}. ${turnText}`;
}

// ===== 3-4 players =====
// Same texts/order as above, but the acting player is named ("Luca ha giocato…")
// and, after every move, the next player is announced ("Tocca a Sara.").
// Actors are seats (A-D). Keyed 'table'/'turn' items supersede each other in the
// single live queue, so fast play never piles up overlapping announcements.
export function buildMoveAnnouncementsMulti({ events, view }) {
  const items = [];
  for (const ev of events) {
    const mine = ev.actor === view.you;
    const who = ev.actor ? playerName(view, ev.actor) : '';
    if (ev.type === 'play') {
      items.push({ sound: 'playCard' });
      if (!mine) items.push({ channel: 'telecronaca', text: `${who} ha giocato ${cardLabel(ev.played)}.` });
    } else if (ev.type === 'capture') {
      items.push({ sound: ev.scopa ? 'scopa' : 'capture' });
      if (!mine) {
        items.push({
          channel: 'telecronaca',
          text: ev.scopa
            ? `${who} ha giocato ${cardLabel(ev.played)} e ha preso tutte le carte sul tavolo. Scopa!`
            : `${who} ha giocato ${cardLabel(ev.played)} e ha preso ${cardsLabel(ev.captured)} con ${cardLabel(ev.played)}.`,
        });
        if (ev.scopa) items.push({ toast: { kind: 'default', title: `Scopa di ${who}`, description: `${who} ha spazzato il tavolo.` } });
      } else if (ev.scopa) {
        items.push({ channel: 'telecronaca', text: 'Scopa!', toast: { kind: 'success', title: 'Scopa!', description: 'Hai spazzato il tavolo!' } });
      }
    } else if (ev.type === 'refill') {
      items.push({ channel: 'telecronaca', text: 'Nuove carte distribuite.' });
    } else if (ev.type === 'handOver') {
      const summary = scoreSummary(view, ev.matchScores);
      items.push({ toast: { kind: 'default', title: 'Fine della mano', description: `Punteggio partita — ${summary}.` } });
      items.push({ channel: 'telecronaca', text: `Fine della mano. Punteggio della partita: ${summary}.` });
    } else if (ev.type === 'gameOver') {
      const won = ev.winner === view.yourSide;
      if (won) items.push({ sound: 'win' });
      let text;
      if (view.numPlayers === 4) text = won ? 'La tua squadra ha vinto la partita!' : `Ha vinto la squadra di ${teamNames(view, ev.winner)}.`;
      else text = won ? 'Hai vinto la partita!' : `${playerName(view, ev.winner)} ha vinto la partita.`;
      items.push({ channel: 'telecronaca', text });
    }
  }
  const moveEv = events.find((e) => e.type === 'play' || e.type === 'capture');
  if (moveEv && view.phase === 'playing') {
    if (moveEv.actor !== view.you) {
      const t = view.table;
      items.push({ channel: 'telecronaca', text: t.length === 0 ? 'Il tavolo è vuoto.' : `Sul tavolo rimangono ${cardsLabel(t)}.`, key: 'table' });
    }
    items.push({ channel: 'status', text: turnText(view), key: 'turn' });
  }
  return items;
}

export function turnText(view) {
  if (view.turn === view.you) return 'È il tuo turno.';
  const partner = isPartner(view, view.turn) ? ', il tuo compagno di squadra' : '';
  return `Tocca ${toName(playerName(view, view.turn))}${partner}.`;
}

export function startAnnouncementMulti(view) {
  const fresh =
    view.phase === 'playing' &&
    view.handNumber === 1 &&
    view.table.length === 4 &&
    view.yourHand.length === 3 &&
    (view.players || []).every((p) => p.cardCount === 3);
  if (!fresh) return null;
  const team =
    view.numPlayers === 4
      ? ` La tua squadra: ${teamNames(view, view.yourSide)}. Avversari: ${teamNames(view, orderedSides(view)[1])}.`
      : '';
  return `Inizia la partita a ${view.numPlayers} giocatori.${team} Sul tavolo ci sono ${cardsLabel(view.table)}. ${turnText(view)}`;
}
